import { useEffect, useRef, useState } from "react";

import {
  clampDelaySeconds,
  clampDurationSeconds,
} from "~/entities/flashPromotionEntities";

export type FlashPromotionPhase = "waiting" | "visible" | "expired";

type Params = {
  /** Whether the card may be shown at all */
  eligible: boolean;
  delaySeconds: number;
  displayDurationSeconds: number;
  /** Freeze both timers - tab hidden, or a redemption in flight */
  paused: boolean;
};

/**
 * waiting --(delay)--> visible --(duration)--> expired
 *
 * The delay starts when eligibility first becomes true, not on mount, so a
 * customer who arrives with a full cart and then empties it still gets the
 * offer. Losing eligibility resets to waiting.
 */
export const useFlashPromotionTimer = ({
  eligible,
  delaySeconds,
  displayDurationSeconds,
  paused,
}: Params): { phase: FlashPromotionPhase; secondsRemaining: number } => {
  const delay = clampDelaySeconds(delaySeconds);
  const duration = clampDurationSeconds(displayDurationSeconds);

  const [phase, setPhase] = useState<FlashPromotionPhase>("waiting");
  const [secondsRemaining, setSecondsRemaining] = useState(duration);

  // Read inside the intervals without making it a dependency, so a pause does
  // not tear down and rebuild the ticker (which would lose the current second)
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(() => {
    if (!eligible) {
      setPhase("waiting");
      setSecondsRemaining(duration);
      return;
    }

    if (phase !== "waiting") {
      return;
    }

    if (delay === 0) {
      setPhase("visible");
      return;
    }

    let elapsed = 0;
    const interval = setInterval(() => {
      if (pausedRef.current) {
        return;
      }
      elapsed += 1;
      if (elapsed >= delay) {
        clearInterval(interval);
        setPhase("visible");
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [eligible, delay, duration, phase]);

  useEffect(() => {
    if (phase !== "visible") {
      return;
    }

    const interval = setInterval(() => {
      if (pausedRef.current) {
        return;
      }
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setPhase("expired");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase]);

  return { phase, secondsRemaining };
};
